<template>
  <component
    :is="component"
    :disabled="disabled || loading"
    class="rounded-4 relative inline-flex cursor-pointer select-none items-center justify-center text-center"
    :class="{
      // Common
      'cursor-default': disabled || loading,
      // Sizes
      'text-17 h-48 px-24 font-semibold': size === 'lg',
      'text-15 h-40 px-16 font-medium': size === 'md',
      'text-13 h-32 px-16 font-semibold': size === 'sm',
      'text-13 font-regular h-24 px-16': size === 'xs',
      // Variant: 'primary'
      'bg-blue-500 text-white': variant === 'primary',
      'hover:bg-blue-600 active:bg-blue-700': variant === 'primary' && !disabled,
      '!bg-blue-100': variant === 'primary' && disabled,
      // Variant: 'secondary'
      'bg-white text-black dark:bg-black dark:text-white': variant === 'secondary',
      'hover:bg-gray-50 active:bg-gray-100 dark:hover:bg-gray-800 dark:active:bg-gray-700':
        variant === 'secondary' && !disabled,
      '!bg-gray-100 dark:!bg-gray-600': variant === 'secondary' && disabled,
      // Variant: 'outlined
      'border border-gray-200 bg-white text-gray-700 dark:border-gray-600 dark:bg-black dark:text-gray-100':
        variant === 'outlined',
      'hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 hover:dark:bg-gray-800 active:dark:bg-gray-700':
        variant === 'outlined' && !disabled,
      '!bg-gray-50 !text-gray-200 dark:!bg-gray-800 dark:!text-gray-600': variant === 'outlined' && disabled,
      // Custom styles
      [`${custom} !border-[--bb-button__border] !bg-[--bb-button__bg] !text-[--bb-button__text] dark:!border-[--bb-button__border--dark] dark:!bg-[--bb-button__bg--dark] dark:!text-[--bb-button__text--dark]`]:
        !!custom && !disabled,
      [`${custom} !border-[--bb-button__border--disabled] !bg-[--bb-button__bg--disabled] !text-white dark:!border-[--bb-button__border--dark--disabled] dark:!bg-[--bb-button__bg--dark--disabled]`]:
        !!custom && disabled,
    }"
  >
    <template v-if="loading">
      <div class="flex items-center justify-center">
        <!-- This invisible slot is needed for the button to remain the same size -->
        <div class="invisible"><slot /></div>
        <bb-spinner :custom="custom" class="absolute !h-[1.4rem] !w-[1.4rem] !border-2" />
      </div>
    </template>
    <template v-else>
      <slot />
    </template>
  </component>
</template>

<script lang="ts" setup>
import { type Component } from 'vue';
import BbSpinner from './BbSpinner.vue';

export type BbButtonBaseProps = {
  disabled?: boolean;
  variant?: 'outlined' | 'plain' | 'primary' | 'secondary';
  size?: 'lg' | 'md' | 'sm' | 'xs';
  loading?: boolean;
  custom?: string;
};

const {
  component = 'button',
  variant = 'primary',
  size = 'md',
  custom = '',
} = defineProps<
  BbButtonBaseProps & {
    component: Component | string;
  }
>();
</script>
